import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { DEFAULT_PAGE_SIZE, PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Avatar,
  Switch,
  FormControlLabel,
  Autocomplete,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import api from "../../services/api";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add,
  Edit,
  Delete,
  Search,
  Close,
  Notifications,
  Send,
  People,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import notificationService from "../../services/notificationService";
import { fetchAllNotifications } from "../../store/slices/notificationSlice";

const typeColors = {
  info: "info",
  warning: "warning",
  success: "success",
  error: "error",
  urgent: "error",
  leave: "warning",
};

const GlobalNotifications = () => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [institutes, setInstitutes] = useState([]);
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    type: "info",
    audience: "all",
    allInstitutes: true,
    instituteIds: [],
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({ id: null, title: "", message: "", type: "info" });
  const [editLoading, setEditLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationService.getGlobalNotifications({ page: 1, limit: 500 });
      const d = res.data?.data || {};
      setRows(d.notifications || []);
      setInstitutes(d.institutes || []);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to load global notifications",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const syncNotificationStore = useCallback(() => {
    dispatch(fetchAllNotifications({}));
  }, [dispatch]);

  const loadNotificationTypes = useCallback(async () => {
    setTypesLoading(true);
    try {
      const res = await api.get("/academic-info/notification-types/all");
      const list = res?.data?.data || [];
      setNotificationTypes(Array.isArray(list) ? list : []);
      const firstKey = list[0]?.key || list[0]?.slug || "info";
      setFormData((p) => ({ ...p, type: p.type || firstKey }));
      setEditFormData((p) => ({ ...p, type: p.type || firstKey }));
    } catch {
      setNotificationTypes([]);
    } finally {
      setTypesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadNotificationTypes();
  }, [loadData, loadNotificationTypes]);

  // Defense in depth: private chat messages are (incorrectly) persisted with
  // scopeType "global" and type "message" (backend moduleNotification.service
  // -> notifyNewMessage). They are not broadcast notifications, so keep them
  // out of this super-admin list even if a stale backend still returns them.
  const isChatMessageNotification = (n) => {
    const type = String(n?.type || "").toLowerCase();
    const entityType = String(n?.metadata?.entityType || "").toLowerCase();
    return type === "message" || entityType === "chat_message";
  };

  const notifications = (rows || [])
    .filter((n) => !isChatMessageNotification(n))
    .map((n) => ({
    id: n.id,
    title: n.title,
    message: n.message || "",
    type: n.type || "info",
    audience: n.audienceLabel || n.scopeType || "—",
    sentBy: n.createdByName || "—",
    sentAt: n.createdAt ? new Date(n.createdAt).toLocaleString() : "—",
    status: n.isRead ? "read" : "unread",
    recipients: typeof n.recipientCount === "number" ? n.recipientCount : "—",
  }));

  const filteredNotifications = searchQuery.trim()
    ? notifications.filter(
        (n) =>
          (n.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (n.message || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (n.sentBy || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (n.audience || "").toLowerCase().includes(searchQuery.toLowerCase())
      )
    : notifications;

  const toggleInstitute = (id) => {
    const idNum = Number(id);
    setFormData((p) => {
      const set = new Set(p.instituteIds || []);
      if (set.has(idNum)) set.delete(idNum);
      else set.add(idNum);
      return { ...p, instituteIds: Array.from(set), allInstitutes: false };
    });
  };

  const openEdit = useCallback((rowId) => {
    const n = rows.find((r) => r.id === rowId);
    if (!n) return;
    setEditFormData({
      id: n.id,
      title: n.title || "",
      message: n.message || "",
      type: n.type || "info",
    });
    setEditDialogOpen(true);
  }, [rows]);

  const columns = useMemo(() => [
    {
      field: "title",
      headerName: "Notification",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            sx={{
              bgcolor: `${typeColors[params.row.type] || "info"}.main`,
              width: 40,
              height: 40,
            }}
          >
            <Notifications />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                maxWidth: 200,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.message}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={typeColors[params.value] || "default"}
        />
      ),
    },
    { field: "sentBy", headerName: "Raised By", width: 140 },
    { field: "audience", headerName: "For (Audience)", width: 220 },
    {
      field: "recipients",
      headerName: "Recipients",
      width: 100,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <People fontSize="small" />
          {typeof params.value === "number" ? params.value.toLocaleString() : params.value}
        </Box>
      ),
    },
    {
      field: "sentAt",
      headerName: "Sent/Scheduled",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params) => {
        const colors = {
          sent: "success",
          read: "default",
          unread: "primary",
          scheduled: "warning",
          draft: "default",
        };
        return (
          <Chip
            label={params.value}
            size="small"
            color={colors[params.value] || "default"}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openEdit(params.row.id);
            }}
          >
            <Edit />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTargetId(params.row.id);
              setDeleteDialogOpen(true);
            }}
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ], [openEdit]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Global Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Send notifications to all institutes and users
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Notification
          </Button>
        </Box>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[
            { label: "Total", value: filteredNotifications.length, color: "primary.main" },
            { label: "Unread", value: filteredNotifications.filter((n) => n.status === "unread").length, color: "warning.main" },
            { label: "Read", value: filteredNotifications.filter((n) => n.status === "read").length, color: "success.main" },
          ].map((stat, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Card>
                <CardContent sx={{ textAlign: "center", py: 2 }}>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ color: stat.color }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card>
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <TextField
                placeholder="Search notifications..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <DataGrid
                rows={filteredNotifications}
                columns={columns}
                autoHeight
                disableRowSelectionOnClick
                pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                initialState={{
                  pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE } },
                }}
                sx={{ border: "none" }}
              />
            )}
          </CardContent>
        </Card>

        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              Create Notification
              <IconButton onClick={() => setCreateDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Type"
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value })
                    }
                    disabled={typesLoading}
                  >
                    {typesLoading ? (
                      <MenuItem disabled>Loading types…</MenuItem>
                    ) : notificationTypes.length === 0 ? (
                      <MenuItem disabled>No types configured</MenuItem>
                    ) : (
                      notificationTypes.map((t) => (
                        <MenuItem key={t.key || t.id} value={t.key}>
                          {t.label || t.key}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Audience</InputLabel>
                  <Select
                    value={formData.audience}
                    label="Audience"
                    onChange={(e) =>
                      setFormData({ ...formData, audience: e.target.value })
                    }
                  >
                    <MenuItem value="all">All Users</MenuItem>
                    <MenuItem value="students">Students Only</MenuItem>
                    <MenuItem value="teachers">Teachers Only</MenuItem>
                    <MenuItem value="admins">Admins Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Target Institutes
                </Typography>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={formData.allInstitutes}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          allInstitutes: e.target.checked,
                          instituteIds: e.target.checked ? [] : p.instituteIds,
                        }))
                      }
                    />
                  )}
                  label="All Institutes"
                />
                {!formData.allInstitutes && (
                  <Autocomplete
                    multiple
                    options={institutes}
                    getOptionLabel={(opt) => opt.name || opt.instituteName || String(opt.id)}
                    value={institutes.filter((inst) => formData.instituteIds.includes(inst.id))}
                    onChange={(_, selected) =>
                      setFormData((p) => ({
                        ...p,
                        instituteIds: selected.map((s) => s.id),
                      }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select institutes"
                        placeholder="Search institutes…"
                      />
                    )}
                    sx={{ mt: 1 }}
                  />
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={createLoading ? null : <Send />}
              disabled={
                createLoading ||
                !formData.title?.trim() ||
                (!formData.allInstitutes && (!formData.instituteIds || formData.instituteIds.length === 0))
              }
              onClick={async () => {
                if (!formData.title?.trim()) return;
                if (!formData.allInstitutes && (!formData.instituteIds || formData.instituteIds.length === 0)) return;
                setCreateLoading(true);
                try {
                  await notificationService.createGlobalNotification({
                    title: formData.title.trim(),
                    message: formData.message?.trim() || "",
                    type: formData.type || "info",
                    audience: formData.audience || "all",
                    allInstitutes: formData.allInstitutes,
                    instituteIds: formData.allInstitutes ? [] : formData.instituteIds,
                  });
                  setSnackbar({ open: true, message: "Global notification sent", severity: "success" });
                  setCreateDialogOpen(false);
                  setFormData({
                    title: "",
                    message: "",
                    type: "info",
                    audience: "all",
                    allInstitutes: true,
                    instituteIds: [],
                  });
                  loadData();
                  syncNotificationStore();
                } catch (err) {
                  setSnackbar({
                    open: true,
                    message: err.response?.data?.message || "Failed to send notification",
                    severity: "error",
                  });
                } finally {
                  setCreateLoading(false);
                }
              }}
            >
              {createLoading ? "Sending…" : "Send Now"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              Edit Notification
              <IconButton onClick={() => setEditDialogOpen(false)}><Close /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData((p) => ({ ...p, title: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={4}
                  value={editFormData.message}
                  onChange={(e) => setEditFormData((p) => ({ ...p, message: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={editFormData.type}
                    label="Type"
                    onChange={(e) => setEditFormData((p) => ({ ...p, type: e.target.value }))}
                    disabled={typesLoading}
                  >
                    {typesLoading ? (
                      <MenuItem disabled>Loading types…</MenuItem>
                    ) : (
                      notificationTypes.map((t) => (
                        <MenuItem key={t.key || t.id} value={t.key}>
                          {t.label || t.key}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={editLoading || !editFormData.id || !editFormData.title?.trim()}
              onClick={async () => {
                if (!editFormData.id || !editFormData.title?.trim()) return;
                setEditLoading(true);
                try {
                  await notificationService.updateNotification(editFormData.id, {
                    title: editFormData.title.trim(),
                    message: editFormData.message?.trim() || "",
                    type: editFormData.type || "info",
                  });
                  setSnackbar({ open: true, message: "Notification updated", severity: "success" });
                  setEditDialogOpen(false);
                  loadData();
                  syncNotificationStore();
                } catch (err) {
                  setSnackbar({
                    open: true,
                    message: err.response?.data?.message || "Failed to update",
                    severity: "error",
                  });
                } finally {
                  setEditLoading(false);
                }
              }}
            >
              {editLoading ? "Saving…" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)}>
          <DialogTitle>Delete notification?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">This removes the notification for all recipients.</Typography>
          </DialogContent>
          <DialogActions>
            <Button disabled={deleteLoading} onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              disabled={deleteLoading || !deleteTargetId}
              onClick={async () => {
                if (!deleteTargetId) return;
                setDeleteLoading(true);
                try {
                  await notificationService.deleteNotification(deleteTargetId);
                  setSnackbar({ open: true, message: "Notification deleted", severity: "success" });
                  setDeleteDialogOpen(false);
                  setDeleteTargetId(null);
                  loadData();
                  syncNotificationStore();
                } catch (err) {
                  setSnackbar({
                    open: true,
                    message: err.response?.data?.message || "Failed to delete",
                    severity: "error",
                  });
                } finally {
                  setDeleteLoading(false);
                }
              }}
            >
              {deleteLoading ? "Deleting…" : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          message={snackbar.message}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default GlobalNotifications;
